// AssistPopover — the in-place help card opened by an assist link in a
// task's text (README §12.2). It mirrors NotePopover's non-modal behaviour
// (overlays the task column, dismisses on Escape / outside click / the close
// button) but carries the external Kitchen Assist payload — a single
// `{ key, value }` — rather than the in-file glossary entries. Kept separate
// from NotePopover so the closed-world chef-hat help and the external assist
// help stay distinct in both content shape and provenance.
//
// Purely presentational: the cooking pane owns the open/close state and
// supplies `entry` (null while the body is still loading).

import { useEffect, useRef } from 'react';
import { theme } from '../theme';

export interface AssistPopoverEntry {
  key: string;
  value: string;
}

export interface AssistPopoverProps {
  /** The matched word as written in the task, for the accessible name. */
  word: string;
  /** The resolved entry, or null while still loading. */
  entry: AssistPopoverEntry | null;
  onClose: () => void;
}

export function AssistPopover({ word, entry, onClose }: AssistPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  const heading = entry?.key ?? word;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Help for ${word}`}
      tabIndex={-1}
      style={{
        position: 'absolute',
        top: 'calc(100% + 0.25rem)',
        left: 0,
        zIndex: 20,
        maxWidth: 'min(22rem, 90vw)',
        background: theme.bg.raised,
        color: theme.text.primary,
        border: `1px solid ${theme.bg.muted}`,
        borderRadius: '10px',
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.18)',
        padding: '0.75rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.6rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{heading}</span>
        <button
          type="button"
          aria-label="Close help"
          onClick={onClose}
          style={{
            width: '32px',
            height: '32px',
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: theme.text.muted,
            cursor: 'pointer',
            fontSize: '1.1rem',
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>
      <span style={{ lineHeight: 1.5 }}>
        {entry ? entry.value : <span style={{ color: theme.text.muted }}>Loading…</span>}
      </span>
    </div>
  );
}

export default AssistPopover;
